import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPayment,
  PAYMENT_MESSAGES,
  mapPaymentError,
  uploadPaymentProof,
} from '../../api/endpoints/payments';
import type { MarkPaidRequest, PaymentMethod, PaymentResponse } from '../../api/types';
import { OrderLabel } from './OrderLabel';
import { formatMoney, parsePaise } from './allocation';
import { ProofInput } from './ProofInput';
import { ProofShareConfirmation } from './ProofShareConfirmation';
import { formatIstDateTime, istDateTimeInput, istInputToIso } from './paymentDates';
import styles from './Payments.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';

export function MarkPaidDialog({
  paymentId,
  payment,
  payeeName,
  onClose,
  onUploadProof = uploadPaymentProof,
  onSubmit,
  onReconcile = getPayment,
  onReconciledPaid,
  submitting = false,
}: {
  paymentId: number;
  payment?: PaymentResponse;
  payeeName?: string | null;
  onClose: () => void;
  onUploadProof?: (proof: File) => Promise<string>;
  onSubmit: (paymentId: number, request: MarkPaidRequest) => void | Promise<void>;
  onReconciledPaid?: () => void | Promise<void>;
  onReconcile?: (paymentId: number) => Promise<PaymentResponse>;
  submitting?: boolean;
}) {
  const { t } = useTranslation();
  const [method, setMethod] = useState<PaymentMethod>('NEFT');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(istDateTimeInput());
  const [note, setNote] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [proofSafeToShare, setProofSafeToShare] = useState(false);
  const [uploadedProof, setUploadedProof] = useState<{ file: File; id: string } | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const valid =
    reference.trim().length >= 6 &&
    reference.trim().length <= 40 &&
    note.trim().length >= 3 &&
    note.trim().length <= 500 &&
    proof != null;
  const inFlight = useRef(false);
  const [blocked, setBlocked] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const safeError = (cause: unknown) => {
    const mapped = mapPaymentError(cause);
    return Object.values(PAYMENT_MESSAGES).includes(mapped)
      ? mapped
      : 'We couldn’t complete this action. Your draft is preserved.';
  };
  const reconcile = async (failureMessage = '') => {
    try {
      const fresh = await onReconcile(paymentId);
      if (fresh.id === paymentId && fresh.direction === 'OUT' && fresh.status === 'PAID') {
        setConfirmed(true);
        await onReconciledPaid?.();
        setSubmitError(null);
      } else {
        setSubmitError(
          failureMessage +
            ' Completion is uncertain or this payout changed. Submission stays blocked; check its status or close for review. An unchanged approved status does not prove the transfer was not recorded.',
        );
      }
    } catch {
      setSubmitError(
        failureMessage +
          ' Could not check payout status. Submission stays blocked. Retry the status check.',
      );
    }
  };
  const submit = async () => {
    if (!proof || !valid || !paidAt || inFlight.current || submitting || blocked || confirmed)
      return;
    inFlight.current = true;
    setLocalSubmitting(true);
    setSubmitError(null);
    const confirmedSafeProof = proofSafeToShare;
    let mutationStarted = false;
    try {
      const transferAt = istInputToIso(paidAt);
      let proofUploadId = uploadedProof?.file === proof ? uploadedProof.id : null;
      if (!proofUploadId) {
        proofUploadId = await onUploadProof(proof);
        setUploadedProof({ file: proof, id: proofUploadId });
      }
      mutationStarted = true;
      await onSubmit(paymentId, {
        method,
        reference: reference.trim(),
        paidAt: transferAt,
        note: note.trim(),
        proofUploadId,
        ...(confirmedSafeProof ? { proofSafeToShare: true } : {}),
      });
      setConfirmed(true);
    } catch (caught) {
      const failure = caught as { status?: number; response?: { status?: number } };
      const status = failure?.status ?? failure?.response?.status;
      if (!mutationStarted) {
        setSubmitError(safeError(caught));
      } else if (status && status >= 400 && status < 500 && status !== 408 && status !== 409) {
        try {
          const fresh = await onReconcile(paymentId);
          const unchanged =
            fresh.id === paymentId &&
            fresh.direction === 'OUT' &&
            fresh.status === 'APPROVED' &&
            (!payment ||
              (fresh.amount === payment.amount &&
                fresh.counterpartyUserId === payment.counterpartyUserId &&
                JSON.stringify(fresh.allocations) === JSON.stringify(payment.allocations)));
          setBlocked(!unchanged);
          setSubmitError(
            safeError(caught) +
              (unchanged
                ? ' The payout is still approved; correct the rejected request before retrying.'
                : ' This payout changed. Submission stays blocked; close and review.'),
          );
        } catch {
          setBlocked(true);
          setSubmitError(
            'Could not verify the rejected payout request. Submission stays blocked; check its status.',
          );
        }
      } else {
        setBlocked(true);
        await reconcile(safeError(caught));
      }
    } finally {
      inFlight.current = false;
      setLocalSubmitting(false);
    }
  };
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.markPaidDialog.dialogLabel')}
        className={`${styles.dialog} ${styles.compact}`}
      >
        <header className={styles.header}>
          <div>
            <h2>{t('payments.markPaidDialog.title')}</h2>
            <p>{t('payments.markPaidDialog.subtitle')}</p>
          </div>
          <DialogHelp context="mark-paid" title={t('payments.markPaidDialog.title')} />
        </header>
        <div className={styles.body}>
          {payment && (
            <div className={styles.card} aria-label={t('payments.markPaidDialog.summaryLabel')}>
              <h3>
                {t('payments.markPaidDialog.summaryTitle', { paymentId, amount: formatMoney(parsePaise(payment.amount)) })}
              </h3>
              <strong>{payeeName ?? 'Payee name unavailable'}</strong>
              <p>
                {payment.payeeAccountMasked ?? 'Account unavailable'} · {t('payments.markPaidDialog.ifsc')}{' '}
                {payment.payeeIfsc ?? 'Unavailable'}
              </p>
              {payment.allocations
                .filter((line) => line.status === 'ACTIVE')
                .map((line) => (
                  <div key={line.id}>
                    <OrderLabel {...line} newTab /> · {line.purpose.toLowerCase()} ·{' '}
                    {formatMoney(parsePaise(line.amount))}
                  </div>
                ))}
            </div>
          )}
          <div className={styles.card}>
            <fieldset
              className={`${styles.grid} ${styles.paidAtGrid}`}
              disabled={submitting || localSubmitting || blocked || confirmed}
            >
              <label className={styles.field}>
                {t('payments.markPaidDialog.method')}
                <select
                  value={method}
                  onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                >
                  {['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'OTHER'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                {t('payments.markPaidDialog.reference')}
                <input
                  aria-label={t('payments.markPaidDialog.referenceAria')}
                  maxLength={40}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
              </label>
              <label className={styles.field}>
                {t('payments.markPaidDialog.paidAt')}
                <DatePicker
                  withTime
                  aria-label={t('payments.markPaidDialog.paidAtAria')}
                  value={paidAt}
                  onChange={setPaidAt}
                />
              </label>
              <label className={`${styles.field} ${styles.full}`}>
                {t('payments.markPaidDialog.note')}
                <textarea
                  aria-label={t('payments.markPaidDialog.note')}
                  rows={3}
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>
              <div className={styles.full}>
                <p>
                  {t('payments.markPaidDialog.proofHint')}
                </p>
                <ProofInput
                  value={proof}
                  onChange={(file) => {
                    setProof(file);
                    setProofSafeToShare(false);
                    setUploadedProof((current) => (current?.file === file ? current : null));
                    setSubmitError(null);
                  }}
                />
                <ProofShareConfirmation
                  checked={proofSafeToShare}
                  onChange={setProofSafeToShare}
                  disabled={!proof || submitting || localSubmitting || blocked || confirmed}
                />
              </div>
            </fieldset>
          </div>
          {paidAt && (
            <p>
              {t('payments.markPaidDialog.transferTime', { time: formatIstDateTime(istInputToIso(paidAt)) })}
            </p>
          )}
          {confirmed && (
            <p role="status">
              {t('payments.markPaidDialog.confirmed')}
            </p>
          )}
          {blocked && !confirmed && (
            <Button
              variant="secondary"
              size="sm"
              disabled={localSubmitting}
              onClick={async () => {
                if (inFlight.current) return;
                inFlight.current = true;
                setLocalSubmitting(true);
                try {
                  await reconcile();
                } finally {
                  inFlight.current = false;
                  setLocalSubmitting(false);
                }
              }}
            >
              {t('payments.markPaidDialog.checkAgain')}
            </Button>
          )}
          {submitError && (
            <div role="alert" className={styles.error}>
              {submitError}
            </div>
          )}
          <div className={styles.actions}>
            <Button
              variant="secondary"
              size="sm"
              disabled={localSubmitting || submitting}
              onClick={onClose}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!valid || !paidAt || submitting || localSubmitting || blocked || confirmed}
              onClick={submit}
            >
              {submitting || localSubmitting
                ? 'Saving…'
                : payment
                  ? `Mark ${formatMoney(parsePaise(payment.amount)).replace(/\.00$/, '')} paid`
                  : 'Mark as paid'}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
